import React, { Fragment, PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Typography, IconButton, Menu, MenuItem, Box } from '@mui/material';
import { ArrowDropDown as SortIcon } from '@mui/icons-material';
import orderBy from 'lodash/orderBy';
import { injectIntl } from 'react-intl';
import UnorderedList from 'components/UnorderedList/UnorderedList';
import styles from './styles.module.scss';

const sortData = (data, field, order) => orderBy(data, [field], [order]);

class SortableHeadedList extends PureComponent {
  static propTypes = {
    intl: PropTypes.object.isRequired,
    intlIds: PropTypes.arrayOf(PropTypes.string).isRequired,
    headingId: PropTypes.string,
    heading: PropTypes.node,
    placeholderId: PropTypes.string.isRequired,
    sortPaths: PropTypes.arrayOf(PropTypes.string).isRequired,
    data: PropTypes.array,
    containerClasses: PropTypes.string,
    containerSx: PropTypes.object,
    listItemPath: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    listItemSecondaryPath: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    listItemPrefix: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    listItemSuffix: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    listItemPrimaryFunc: PropTypes.func,
    listItemSecondaryFunc: PropTypes.func,
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    if (nextProps.data !== prevState.data) {
      return {
        sortedData: sortData(nextProps.data, prevState.sortField, prevState.sortOrder),
        data: nextProps.data,
      };
    }
    return null;
  }

  state = {
    anchorEl: null,
    sortOrder: 'asc', // default sort order
    sortField: this.props.sortPaths[0], // default sort field
    sortedData: sortData(this.props.data, this.props.sortPaths[0], 'asc'),
    // eslint-disable-next-line react/no-unused-state
    data: this.props.data,
    headingHovered: false,
  };

  handleFieldClick = () => {
    const { sortOrder, sortField } = this.state;
    const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    const sortedData = sortData(this.props.data, sortField, newOrder);
    this.setState({ sortOrder: newOrder, sortedData });
  };

  handleMenuOpen = event => {
    this.setState({ anchorEl: event.currentTarget });
  };

  handleMenuClose = () => {
    this.setState({ anchorEl: null });
  };

  handleMenuItemClick = field => {
    const { sortOrder } = this.state;
    const sortedData = sortData(this.props.data, field, sortOrder);
    this.setState({ sortField: field, sortedData, anchorEl: null });
  };

  handleHeadingMouseEnter = () => {
    this.setState({ headingHovered: true });
  };

  handleHeadingMouseLeave = () => {
    this.setState({ headingHovered: false });
  };

  render() {
    const { anchorEl, sortOrder, sortField, sortedData, headingHovered } = this.state;
    const {
      intlIds,
      sortPaths,
      listItemPath,
      listItemSecondaryPath,
      listItemPrefix,
      listItemSuffix,
      listItemPrimaryFunc,
      listItemSecondaryFunc,
      intl,
      headingId,
      placeholderId,
      heading,
      containerClasses: containerClassesProp,
      containerSx: containerSxProp,
    } = this.props;
    const containerClasses = [
      styles.container,
      ...(containerClassesProp ? [containerClassesProp] : []),
      headingHovered ? styles.headingHovered : '',
    ]
      .filter(Boolean)
      .join(' ');
    // An explicit containerSx always wins. Otherwise, the bootstrap-token
    // default layout only ever applied when no override class was passed in —
    // mirrors the old array's conditional branch exactly.
    const containerSx =
      containerSxProp ||
      (containerClassesProp
        ? undefined
        : {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'center',
            mt: 3,
            mr: 0.5,
          });

    return (
      <Fragment>
        <Box
          className={containerClasses}
          sx={containerSx}
          onMouseEnter={this.handleHeadingMouseEnter}
          onMouseLeave={this.handleHeadingMouseLeave}
        >
          <Typography
            variant="h6"
            component="div"
            onClick={this.handleFieldClick}
            className={styles.typography}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            {heading}
            {!heading && headingId && intl.formatMessage({ id: headingId })}
          </Typography>
          <Box
            className={styles.sortInfo}
            sx={{ display: 'flex', flexGrow: 1, alignItems: 'center', justifyContent: 'space-between' }}
          >
            <Typography variant="caption" onClick={this.handleFieldClick}>
              {sortOrder === 'asc' ? '▲' : '▼'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
              <Typography
                variant="subtitle1"
                onClick={this.handleMenuOpen}
                sx={{ mr: 1 }}
              >
                {`${intl.formatMessage({ id: intlIds[sortPaths.indexOf(sortField)] })}`}
              </Typography>
              <IconButton
                size="small"
                color="default"
                aria-label="sort"
                onClick={this.handleMenuOpen}
                className={styles.iconButton}
              >
                <SortIcon />
              </IconButton>
            </Box>
          </Box>
          <Menu
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={this.handleMenuClose}
          >
            {sortPaths.map((path, index) => (
              <MenuItem key={path} onClick={() => this.handleMenuItemClick(path)}>
                {intl.formatMessage({ id: intlIds[index] })}
              </MenuItem>
            ))}
          </Menu>
        </Box>
        <UnorderedList
          data={sortedData}
          itemPath={listItemPath}
          secondaryItemPath={listItemSecondaryPath}
          primaryFunc={listItemPrimaryFunc}
          secondaryFunc={listItemSecondaryFunc}
          placeholder={intl.formatMessage({ id: placeholderId })}
          prefix={listItemPrefix}
          suffix={listItemSuffix}
        />
      </Fragment>
    );
  }
}

export default injectIntl(SortableHeadedList);
